import { StatusIndicator } from '@diligentcorp/atlas-theme-mui-lens';
import React, { useMemo, useState } from 'react';
import { ButtonGroup } from 'src/components/atoms';
import { DropdownSelect } from 'src/components/molecules';
import { EmployeeDirectoryFieldOption } from 'src/components/molecules/DropdownSelect/components/EmployeeDirectoryFieldOption';
import type { UserType } from 'src/graphql';
import { useSearchUsers } from 'src/hooks';
import { FormState } from 'src/types';
import { overdueMitigationColorResolver } from 'src/components/atlas/organisms/helpers/sharedStatusColorResolvers';
import { commitExternalOffenderValue } from './commitExternalOffenderValue';
import {
  DatabaseFieldModeContainer,
  DynamicQuestionDescription,
  DynamicQuestionLabel,
} from './DynamicQuestion.styles';
import {
  COI_EMPLOYEE_DIRECTORY_SUBTYPE_KEYS,
  DATABASE_EMPLOYEE_DIRECTORY_FIELDS,
  DynamicQuestionProps,
} from './DynamicQuestionProps';
import { ExternalOffenderInput } from './ExternalOffenderInput';
import { DynamicQuestionAnswerValue } from './types';

type DatabaseFieldOption = {
  label: string;
  value: string;
  user: UserType;
  role?: string | null;
  department?: string | null;
  country?: string | null;
};

interface DynamicQuestionDatabaseFieldProps {
  question: DynamicQuestionProps['question'];
  value: DynamicQuestionAnswerValue;
  onChange: (value: DynamicQuestionAnswerValue) => void;
  baseTestId: string;
  label: string;
  description: string | undefined;
  isRequired: boolean;
  isFlagged?: boolean | null;
}

export const DynamicQuestionDatabaseField: React.FC<
  DynamicQuestionDatabaseFieldProps
> = ({
  question,
  value,
  onChange,
  baseTestId,
  label,
  description,
  isRequired,
  isFlagged = false,
}) => {
  const [databaseFieldMode, setDatabaseFieldMode] = useState<
    'Employee' | 'External'
  >(() => {
    const hasExternalName = (value.textValues ?? []).some(
      (v) => !/^\d+$/.test(v),
    );
    return hasExternalName ? 'External' : 'Employee';
  });
  const [externalOffenderInput, setExternalOffenderInput] = useState('');

  const subTypeKey = question.databaseFieldSubType?.subTypeKey;
  const isNamedOffenderField = subTypeKey === 'named_offender';
  const isCoiEmployeeDirectoryField =
    COI_EMPLOYEE_DIRECTORY_SUBTYPE_KEYS.includes(subTypeKey || '');
  const isEmployeeDirectoryField = DATABASE_EMPLOYEE_DIRECTORY_FIELDS.includes(
    subTypeKey || '',
  );

  const isEmployeeId = (v: string) => /^\d+$/.test(v);

  const employeeIdList = useMemo(
    () =>
      isEmployeeDirectoryField
        ? (value.textValues ?? []).filter(isEmployeeId)
        : [],
    [isEmployeeDirectoryField, value.textValues],
  );

  const externalNameList = useMemo(
    () =>
      isEmployeeDirectoryField
        ? (value.textValues ?? []).filter((v) => !isEmployeeId(v))
        : [],
    [isEmployeeDirectoryField, value.textValues],
  );

  const { searchUsers, sortedValidUsers } = useSearchUsers(
    [],
    [],
    false,
    false,
    null,
    undefined,
    undefined,
    employeeIdList.length > 0 ? employeeIdList : undefined,
    isEmployeeDirectoryField && employeeIdList.length > 0,
  );

  const databaseFieldOptions = useMemo<DatabaseFieldOption[]>(() => {
    if (!isEmployeeDirectoryField) return [];
    return (sortedValidUsers || []).map((user) => ({
      label: user.name || 'Anonymous',
      value: String(user.id),
      user: user as UserType,
      role: user.role,
      department: user.department,
      country: user.country,
    }));
  }, [sortedValidUsers, isEmployeeDirectoryField]);

  const selectedDatabaseFieldOption = useMemo(() => {
    if (employeeIdList.length === 0) {
      return [] as DatabaseFieldOption[];
    }
    return employeeIdList
      .map((id) => databaseFieldOptions.find((o) => o.value === id))
      .filter((o): o is DatabaseFieldOption => o != null);
  }, [databaseFieldOptions, employeeIdList]);

  const commitExternalOffender = () => {
    if (!externalOffenderInput.trim()) return;
    const nextValues = commitExternalOffenderValue(
      externalOffenderInput,
      value.textValues,
      Boolean(question.allowMultiSelect),
    );
    onChange({ ...value, textValues: nextValues });
    setExternalOffenderInput('');
  };

  return (
    <div data-testid={baseTestId}>
      <DynamicQuestionLabel>
        <div>
          {label}
          {isRequired && (
            <span className="required" aria-hidden>
              {' '}
              (Required)
            </span>
          )}
        </div>
        {isFlagged && (
          <StatusIndicator
            customColor={overdueMitigationColorResolver}
            label={'Flagged answer'}
            aria-label={'Flagged answer'}
          />
        )}
      </DynamicQuestionLabel>
      {description && (
        <DynamicQuestionDescription>{description}</DynamicQuestionDescription>
      )}
      {isNamedOffenderField && (
        <DatabaseFieldModeContainer>
          <ButtonGroup
            options={['Employee', 'External'] as const}
            selectedOption={databaseFieldMode}
            setSelectedOption={(mode) => {
              setDatabaseFieldMode(mode);
              if (!question.allowMultiSelect) {
                onChange({ ...value, textValues: undefined });
              }
            }}
            size="small"
            data-testid={`${baseTestId}-mode-toggle`}
          />
        </DatabaseFieldModeContainer>
      )}
      {(!isNamedOffenderField || databaseFieldMode === 'Employee') && (
        <DropdownSelect<DatabaseFieldOption>
          isMulti={question.allowMultiSelect ?? false}
          isSearchable={true}
          state={FormState.DEFAULT}
          onInputChange={(input: string) => searchUsers(input)}
          disableFilter={true}
          options={databaseFieldOptions}
          value={selectedDatabaseFieldOption}
          components={
            isCoiEmployeeDirectoryField
              ? { Option: EmployeeDirectoryFieldOption }
              : undefined
          }
          onChange={(option) => {
            if (question.allowMultiSelect) {
              const arr = Array.isArray(option)
                ? option
                : option
                  ? [option]
                  : [];
              const selectedIds = arr
                .filter(
                  (o): o is DatabaseFieldOption =>
                    o != null && typeof o === 'object' && 'user' in o,
                )
                .map((o) => String(o.user.id));
              onChange({
                ...value,
                textValues: [...selectedIds, ...externalNameList],
              });
              return;
            }
            const single = Array.isArray(option) ? option[0] : option;
            if (single && typeof single === 'object' && 'user' in single) {
              onChange({
                ...value,
                textValues: [String(single.user.id)],
              });
            } else {
              onChange({ ...value, textValues: undefined });
            }
          }}
          placeholder="Select from directory"
          buttonVariant="secondary"
          data-testid={`${baseTestId}-database-field`}
        />
      )}
      {isNamedOffenderField && databaseFieldMode === 'External' && (
        <ExternalOffenderInput
          allowMultiSelect={question.allowMultiSelect ?? false}
          inputValue={externalOffenderInput}
          onInputChange={setExternalOffenderInput}
          onCommit={commitExternalOffender}
          externalNames={externalNameList}
          onRemove={(name) => {
            onChange({
              ...value,
              textValues: (value.textValues ?? []).filter((v) => v !== name),
            });
          }}
          baseTestId={baseTestId}
        />
      )}
    </div>
  );
};
